import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { map, filter, reduce, findIndex, cloneDeep, first } from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import ReactResizeDetector from 'react-resize-detector';
import SearchField from '../../../../components/search-field';
import ProgressBar from '../../../../components/progress-bar';
// Import Actions
import { 
  requestFetchAnnualRevenueFields,
  requestUpdateAnnualRevenueFields, 
} from '../../SetupActions';

// Import Selectors
import { getViewableDimensions } from '../../../App/AppReducer';
import DataTypeCellRenderer from '../../components/cells/DataTypeCellRenderer';
import ToggleCellRenderer from '../../components/cells/ToggleFieldRenderer';
import APICellRenderer from '../../components/cells/APICellRenderer';
import CustomHeader from '../../../../components/god-table/header';
import FieldLabelCellRenderer from '../../components/cells/FieldLabelCellRenderer';

class AnnualRevenuePage extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      loaded: false,
      error: null,
      allUpdating: false,
      fields: [],
      dirty: false,
      navBarHeight: 0,
    };
    this.onGridReady = this.onGridReady.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.loadCustomFields = this.loadCustomFields.bind(this);
    this.markAsAnnualRevenueField = this.markAsAnnualRevenueField.bind(this);
    this.submit = this.submit.bind(this);
    this.onResizNavBar = this.onResizNavBar.bind(this);
  }

  componentDidMount() {
    this.loadCustomFields();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }
  onResizNavBar(width, navBarHeight) {
    this.setState({ navBarHeight });
  }
  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }

  loadFields(rawFields) {
    return reduce(rawFields || [], (items, field) => {
      if (field.sync_required === true || field.is_annual_revenue_field === true) {
        const f = cloneDeep(field);
        if (f.name === 'AnnualRevenue') {
          f.is_default = true;
        }
        f.original_is_annual_revenue_field = f.is_annual_revenue_field;
        items.push(f);
      } else if (field.accessible === false) {
        const f = cloneDeep(field);
        // so that user can remove manually
        f.original_is_annual_revenue_field = f.is_annual_revenue_field;
        items.push(f);
      }
      return items;
    }, []);
  }

  loadCustomFields() {
    this.setState({
      loading: true, error: null, loaded: false, fields: [],
    });
    const allFieldsPromise = this.props.dispatch(requestFetchAnnualRevenueFields());
    allFieldsPromise.then(
      (results) => {
        // clone!!!!! results.fields
        const fields = this.loadFields(results.fields);
        this.setState({
          loading: false, error: null, loaded: true, fields,
        });
      },
      (error) => {
        this.setState({
          loading: false, error, loaded: false, fields: [],
        });
      },
    );
    return allFieldsPromise;
  }

  markAsAnnualRevenueField(field) {
    const { fields } = this.state;
    const newFields = fields.map((f) => {
      if (f._id === field._id) {
        return { ...f, is_annual_revenue_field: true };
      }
      return { ...f, is_annual_revenue_field: false };
    });
    const dirtyFields = filter(newFields, f => f.original_is_annual_revenue_field !== f.is_annual_revenue_field);
    const dirty = dirtyFields.length > 0;
    this.setState({ dirty, fields: newFields }, () => {
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    });
  }

  submit() {
    const { fields, allUpdating } = this.state;
    if (allUpdating) {
      return;
    }
    this.setState({ allUpdating: true });
    const selectedField = first(map(filter(fields, { is_annual_revenue_field: true }), 'name'));
    this.props.dispatch(requestUpdateAnnualRevenueFields({ annual_revenue_field: selectedField }))
      .then(
        (results) => {
          const newFields = this.loadFields(results.fields);
          this.setState({ allUpdating: false, fields: newFields, dirty: false });
        },
        () => {
          this.setState({ allUpdating: false });
        },
      );
  }


  renderTable() {
    const { viewableDimensions } = this.props;
    const {
      fields, allUpdating, dirty,
      navBarHeight,
    } = this.state;

    if (fields.length === 0) {
      return (
        <div className="alert alert-danger">
          No custom fields of type <code>currency</code> found.
        </div>
      );
    }

    const columnDefs = [
      {
        headerName: 'Field Name',
        field: 'label',
        suppressMenu: true,
        width: 450,
        suppressMovable: true,
        cellRenderer: FieldLabelCellRenderer,
        pinned: 'left',
      },
      {
        headerName: 'API Name',
        field: 'name',
        suppressMenu: true,
        width: 365,
        cellRenderer: APICellRenderer,
        suppressMovable: true,
      },
      {
        headerName: 'Data Type',
        field: 'type',
        suppressMenu: true,
        width: 120,
        suppressMovable: true,
        cellRenderer: DataTypeCellRenderer,
      },
      {
        headerName: 'Use for Annual Revenue',
        field: 'is_annual_revenue_field',
        suppressMenu: true,
        width: 200,
        suppressMovable: true,
        cellRenderer: ToggleCellRenderer,
        cellRendererParams: {
          onToggle: this.markAsAnnualRevenueField,
          disabled: allUpdating,
          isRadio: true,
        },
      },  
    ];

    const viewableHeight = viewableDimensions.height - navBarHeight;
    return (
      <div>
        {fields.length > 0 && 
          <nav className="navbar navbar-default navbar-controls">
            <form className="navbar-form navbar-left">
              <SearchField collapsible={false} onSearch={this.handleSearch} />
            </form>
            {dirty &&
              <div className="navbar-form navbar-right animated flash" style={{ marginRight: 5 }}>
                <button onClick={this.submit} disabled={allUpdating} className="btn btn-primary">
                  Save &amp; Update
                </button>
              </div>
            }
            <ReactResizeDetector handleHeight onResize={this.onResizNavBar} />
          </nav>
        }
        <div className="ag-theme-alpine" style={{ height: viewableHeight, width: '100%' }}>
          <AgGridReact
            reactNext
            enableColResize
            enableSorting
            animateRows
            columnDefs={columnDefs}
            getRowNodeId={data => data._id}
            onGridReady={this.onGridReady}
            rowData={fields}
          />
        </div>
      </div>
    );
  }


  render() {
    const { loading, loaded } = this.state;
    return (
      <div>
        <Helmet title="Annual Revenue | Account Size" />
        <div className="container">
          {loading && <ProgressBar />}
          {!loading && loaded && this.renderTable()}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    viewableDimensions: getViewableDimensions(state),
  };
}

AnnualRevenuePage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  viewableDimensions: PropTypes.shape({}).isRequired,
};

AnnualRevenuePage.contextTypes = {
  router: PropTypes.object,
};

export default connect(mapStateToProps)(AnnualRevenuePage);
